// components/CreationBDC.tsx
import React, { useEffect, useState } from "react";
import { Button, Form, Space, Spin, Table, Tabs } from "antd";
import type { TabsProps } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { UserOutlined } from '@ant-design/icons';
import {
  Typography, Box, CircularProgress,
} from "@mui/material";
import axios from "axios";
import { IBonDeCaisse, ICompte, IParametreBDC, ISimulationBDC2 } from "../../../dataTypes/GlobalDataType";
import { Crypter } from "../../../Functions/Security/Crypter";
import Data from '../../../Data/Data.json';
import { useAuthHeader, useAuthUser } from '../../../Services/Auth/sessionAuth';
import Utilisateur from "../../../Models/Utilisateur";
import { fetchDataTable2, GlobalExportToCSV, GlobalExportToExcel, GlobalExportToJSON, GlobalExportToPDF, GlobalExportToWord } from "../../../utilitaires/globalFunction";
import formatNumber from "../../../Functions/Traitement/FormatNumber";
import SweetAlert from "sweetalert2";
import ParamsPicklist from "../../../Types/ParamsPicklist";
import DataSelect from "../../../Types/DataSelect";
import InputForm from "../../InputT";
import SearchInput from "../../SearchInput";
import SearchModal from "../../SearchModal";
import SelectOption from "../../SelectOption";
import ExportMenu from "../../ExportMenu";

export default function CreationBDC() {
  const auth = useAuthUser()
  const authHeader = useAuthHeader()
  const Perss: Utilisateur = JSON.parse(JSON.stringify(auth()))

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<IBonDeCaisse[]>([]);
  const [selected, setSelected] = useState<IBonDeCaisse | null>(null);

  const [sbmit, setSbmit] = useState("");

  const [simulations, setSimulations] = useState<ISimulationBDC2[]>([]);
  const [loadingSimul, setLoadingSimul] = useState(false);
  const [paramBDC, setParamBDC] = useState<IParametreBDC[]>([]);
  const [selectedType, setSelectedType] = useState<IParametreBDC | null>(null);
  const [compte, setCompte] = useState<ICompte[]>([]);
  const [plansAutorises, setPlansAutorises] = useState<string[]>([]);

  // Boutons (même pilotage que /prets/simulation)
  const [ajoutActve, setAjoutActif] = useState(false)
  const [modifActve, setModifActve] = useState(true)
  const [enregActve, setEnregActve] = useState(true)
  const [impActive, setImpActive] = useState(true)
  const [annulActive, setAnnulActive] = useState(true)
  const [consulActive, setConsulActive] = useState(false)

  const [echeancier, setEcheancier] = useState<{
    periode: number;
    dateImput: string;
    interet: number;
    taxe: number;
    netAPayer: number;
  }[]>([]);

  // Picklists
  // Recherche fusionnée N° BDC (consultation) / code simulation (souscription)
  let searchCodeBdc: ParamsPicklist = { CodeAgence: Perss.codage, Table: "V_BDC_RECHERCHE", Colonne1: "CODE", Colonne2: "LIBELLE", Colagence: 'CODAGE', Choix: 22 }
  let searchBdc: ParamsPicklist = { CodeAgence: Perss.codage, Table: "BONDECAISSE", Colonne1: "NUMBDC", Colonne2: "NOMCLI", Colagence: 'CODAGE', Choix: 22 }
  // Création directe (sans simulation) : client, type de BDC, périodicité
  let searchClient: ParamsPicklist = { CodeAgence: Perss.codage, Table: "CLIENT", Colonne1: "NUMCLI", Colonne2: "NOMCLI", Colagence: 'CODAGE', Choix: 22 }
  let searchTypeBdc: ParamsPicklist = { CodeAgence: Perss.codage, Table: "PARAMBDC", Colonne1: "CODBDC", Colonne2: "LIBELL", Colagence: 'CODAGE', Choix: 22 }
  let searchFreq: ParamsPicklist = { CodeAgence: '', Table: "LIB", Colonne1: "CODLIB", Colonne2: "LIBELL", colfiltre: "RUBRIQ", valfiltre: 'PERIOD', Choix: 22 }

  const columns: ColumnsType<any> = [
    { title: 'Période', dataIndex: 'periode', key: 'periode', width: 80 },
    { title: 'Date imputation', dataIndex: 'dateImput', key: 'dateImput', width: 120 },
    { title: 'Intérêt par période', dataIndex: 'interet', key: 'interet', align: 'right', render: (v: number) => formatNumber(Number(v)) },
    { title: 'Taxe par période', dataIndex: 'taxe', key: 'taxe', align: 'right', render: (v: number) => formatNumber(Number(v)) },
    { title: 'Net par période', dataIndex: 'netAPayer', key: 'netAPayer', align: 'right', render: (v: number) => formatNumber(Number(v)) },
  ];

  // ------------------------------------------------------------------
  // Pilotage des boutons (identique à /prets/simulation)
  // ------------------------------------------------------------------
  function initialiazeButton() {
    setAjoutActif(false)
    setModifActve(true)
    setEnregActve(true)
    setImpActive(true)
    setAnnulActive(true)
    setConsulActive(false)
  }

  const SetStart = () => {
    setSbmit("add")
    form.resetFields()
    form.setFieldsValue({ DATDEBUT: Perss.datjour, DATFIN: Perss.datjour, CDETAT: '01' })
    setSelected(null)
    setSelectedType(null)
    setEcheancier([])
    setCompte([])
    setPlansAutorises([])
    setAjoutActif(true)
    setEnregActve(false)
    setAnnulActive(false)
    setConsulActive(true)
  }

  function handleAnulle() {
    setImpActive(true)
    setData([])
    setEcheancier([])
    setSelected(null)
    setCompte([])
    setSbmit("")
    form.resetFields()
    form.setFieldsValue({ DATDEBUT: Perss.datjour, DATFIN: Perss.datjour })
    initialiazeButton()
  }

  function cunsultButton() {
    setAnnulActive(false)
    setModifActve(false)
    setEnregActve(true)
    setConsulActive(true)
  }

  function modifButton() {
    setSbmit("update")
    setModifActve(true)
    setAjoutActif(true)
    setEnregActve(false)
    setConsulActive(true)
  }

  // ------------------------------------------------------------------
  // Chargement des listes
  // ------------------------------------------------------------------
  const fetchData = async (): Promise<IBonDeCaisse[]> => {
    try {
      var params = { CodeAgence: Perss.codage, Table: "BONDECAISSE", Colonne1: "NUMCLI", Colonne2: "NOMCLI", Colagence: "CODAGE", Init: "", choix: 32, colfiltre: "", valfiltre: "" }
      const res = await fetchDataTable2(Data.ApiUrl.AnySearch, params, authHeader());
      const list: IBonDeCaisse[] = res || []
      setData(list);
      return list
    } catch (err) {
      console.error("Erreur de chargement:", err);
      return []
    }
  };

  const fetchDataTypeBDC = async () => {
    try {
      var params = { CodeAgence: Perss.codage, Table: "PARAMBDC", Colonne1: "CODBDC", Colonne2: "LIBELL", Colagence: "CODAGE", Init: "", choix: 32, colfiltre: "", valfiltre: "" }
      const res = await fetchDataTable2(Data.ApiUrl.AnySearch, params, authHeader());
      if (res.length !== 0) {
        setParamBDC(res);
      }
    } catch (err) {
      console.error("Erreur de chargement:", err);
    }
  };

  // Charger les simulations en statut 01 (retourne la liste fraîche)
  const getSimulations = async (): Promise<ISimulationBDC2[]> => {
    try {
      var params = { CodeAgence: Perss.codage, Table: "SIMULATIONBDC", Colonne1: "", Colonne2: "", Colagence: "CODAGE", Init: "", choix: 32, colfiltre: "", valfiltre: "" }
      const res = await fetchDataTable2(Data.ApiUrl.AnySearch, params, authHeader());
      const list: ISimulationBDC2[] = (res || []).filter((c: ISimulationBDC2) => !c.CDETAT || c.CDETAT === "01")
      setSimulations(list);
      return list;
    } catch (err) {
      console.error("Erreur de chargement:", err);
      return [];
    }
  };

  // Charger les comptes du client
  const getComptes = async (numcli: string) => {
    setCompte([])
    try {
      var params = { CodeAgence: Perss.codage, Table: "COMPTES", Colonne1: "NUMCLI", Colonne2: "NOMCPT", Colagence: "AGCCPT", Init: numcli, choix: 34 }
      const res = await fetchDataTable2(Data.ApiUrl.AnySearch, params, authHeader());
      const list: ICompte[] = res || []
      // Catégories D/P en premier, mais tous les comptes du client restent proposés
      const principaux = list.filter(c => c.categocpt?.toLocaleUpperCase().includes("D") || c.categocpt?.toLocaleUpperCase().includes("P"));
      const autres = list.filter(c => !(c.categocpt?.toLocaleUpperCase().includes("D") || c.categocpt?.toLocaleUpperCase().includes("P")));
      setCompte([...principaux, ...autres]);
      if (list.length === 0) console.warn("Aucun compte trouvé pour le client : " + numcli);
    } catch (err) {
      console.error("Erreur de chargement:", err);
    }
  };

  const fmtDate = (d: any) => d ? new Date(d).toISOString().substring(0, 10) : "";

  // Plans comptables autorisés du type (comptes dépôt / intérêts)
  const fetchPlansAutorises = async (codbdc?: string) => {
    if (!codbdc) { setPlansAutorises([]); return; }
    try {
      var params = { CodeAgence: Perss.codage, Table: "PARAMBDC_PLANS", Colonne1: "IDPLAN", Colonne2: "IDPLAN", Colagence: "CODAGE", Init: "", choix: 32, colfiltre: "CODBDC", valfiltre: codbdc }
      const res = await fetchDataTable2(Data.ApiUrl.AnySearch, params, authHeader());
      setPlansAutorises((res || []).map((p: any) => p.IDPLAN));
    } catch (err) {
      console.error("Erreur chargement plans:", err);
    }
  };

  // Champ fusionné N° BDC / Code Simul : la sélection ne fait que poser le
  // code — le chargement se fait au clic sur « Consulter » (flux standard).

  // Remplit le formulaire depuis une simulation (souscription)
  const fillFromSimul = (simul: ISimulationBDC2) => {
      const typ = paramBDC.find(t => t.CODBDC === simul.TYPBDC);
      setSelectedType(typ || null)
      form.setFieldsValue({
        CODSIMUL: simul.CODSIMUL,
        NUMCLI: simul.NUMCLI,
        NOMCLI: simul.NOMCLI,
        lib_NUMCLI: simul.NOMCLI,
        TYPBDC: simul.TYPBDC,
        lib_TYPBDC: typ?.LIBELL || '',
        TYPIMPUTINT: simul.TYPIMPUTINT,
        FREQPROV: simul.FREQPROV,
        DUREE: simul.DUREE,
        DATDEBUT: fmtDate(simul.DATDEBUT),
        DATFIN: fmtDate(simul.DATFIN),
        MONTANT: simul.MONTANT || 0,
        TXINT: simul.TXINT || 0,
        MTNINT: simul.MTNINT || 0,
        TXTAXE: simul.TXTAXE || 0,
        MTNTAXE: simul.MTNTAXE || 0,
        MTNNETAPAYER: simul.MTNNETAPAYER || 0,
        MTNPROVFREQ: simul.MTNPROVFREQ || 0,
        MTNTAXEFREQ: simul.MTNTAXEFREQ || 0,
        // Comptes GL d'imputation repris du type de BDC
        CPTINT: typ?.CPTINT || "",
        CPTPROV: typ?.CPTPROV || "",
        CPTTAXEDEB: typ?.CPTTAXEDEB || "",
        CPTTAXECRE: typ?.CPTTAXECRE || "",
      });
      getComptes(simul.NUMCLI)
      fetchPlansAutorises(simul.TYPBDC)
      calculer(simul)
      fetchFrais(simul.TYPBDC, simul.MONTANT)
    }

  // Aperçu frais de souscription : grille du CODOPEFRAIS du type appliquée
  // au montant déposé (même mécanisme que Safe Care)
  const fetchFrais = async (typBdc?: string, montant?: any) => {
    if (!typBdc || !Number(montant)) {
      form.setFieldValue('FRAISSOUSCR', '');
      return;
    }
    try {
      const response = await axios({
        method: 'get',
        maxBodyLength: Infinity,
        url: Data.ApiUrl.ApercuFraisBDC,
        headers: {
          'CypherTypBdc': Crypter.encrypt(JSON.stringify(typBdc)),
          'CypherAgence': Crypter.encrypt(JSON.stringify(Perss.codage)),
          'CypherMontant': Crypter.encrypt(JSON.stringify(Number(montant))),
          'Authorization': authHeader(),
          'Content-Type': 'application/json'
        }
      });
      if (response.data) {
        const res = JSON.parse(Crypter.decrypt(response.data));
        const status = res?.Result?.Table?.[0];
        const detail = res?.Result?.Table1?.[0];
        if (String(status?.Status) === '200') {
          const frais = Number(detail?.Frais) || 0;
          form.setFieldValue('FRAISSOUSCR', frais > 0 ? formatNumber(frais) : 'Aucun');
        } else {
          form.setFieldValue('FRAISSOUSCR', status?.result || 'Indisponible');
        }
      }
    } catch (err) {
      console.warn('ApercuFrais - réponse inattendue', err);
      form.setFieldValue('FRAISSOUSCR', 'Indisponible');
    }
  };

  // Sélection compte → récupère le disponible via GetCompte (comme BlocageComptes)
  const fetchDispon = async (numcpt: any, dispo: string) => {
    if (!numcpt) {
      form.setFieldValue(dispo, "");
      return;
    }
    try {
      const crypted = Crypter.encrypt(String(numcpt));
      const response = await axios({
        method: 'get',
        maxBodyLength: Infinity,
        url: Data.ApiUrl.GetCompte,
        headers: {
          'CypherNumCpte': crypted,
          'Authorization': authHeader(),
          'Content-Type': 'application/json'
        }
      });
      if (response.data) {
        const res = JSON.parse(Crypter.decrypt(response.data));
        if (res.StatutCode == 200 && res.Result?.Table?.[0] != null) {
          form.setFieldValue(dispo, formatNumber(Number(res.Result.Table[0].DISPON) || 0));
        } else {
          console.warn("GetCompte - réponse inattendue:", res);
        }
      }
    } catch (error) {
      console.log("Erreur GetCompte:", error);
    }
  }

  // Watchers : le disponible suit automatiquement le compte sélectionné
  const cptDepot = Form.useWatch('CPTDEPOT', form);
  const cptBdc = Form.useWatch('CPTBDC', form);
  const cptIntCli = Form.useWatch('CPTINTCLI', form);
  useEffect(() => { fetchDispon(cptDepot, 'DISPONCPTDEPOT') }, [cptDepot]);
  useEffect(() => { fetchDispon(cptBdc, 'DISPONCPTBDC') }, [cptBdc]);
  useEffect(() => { fetchDispon(cptIntCli, 'DISPONCPTINT') }, [cptIntCli]);

  // Création directe : sélection du client -> charge ses comptes
  const numCli = Form.useWatch('NUMCLI', form);
  useEffect(() => { if (numCli && sbmit === 'add') getComptes(numCli); }, [numCli]);

  // Sélection du type -> reporte les paramètres par défaut (imputation,
  // fréquence, durée, taux, comptes GL) + plans autorisés
  const typBdc = Form.useWatch('TYPBDC', form);
  useEffect(() => {
    const typ = paramBDC.find(t => t.CODBDC === typBdc);
    setSelectedType(typ || null);
    if (typ && sbmit === 'add') {
      form.setFieldsValue({
        TYPIMPUTINT: typ.TYPIMPUTINT,
        FREQPROV: typ.FREQPROV,
        DUREE: typ.DUREE,
        TXINT: typ.TXINT,
        TXTAXE: typ.TXTAXE,
        CPTINT: typ.CPTINT || '',
        CPTPROV: typ.CPTPROV || '',
        CPTTAXEDEB: typ.CPTTAXEDEB || '',
        CPTTAXECRE: typ.CPTTAXECRE || '',
      });
      fetchPlansAutorises(typ.CODBDC);
    }
  }, [typBdc]);

  // Date fin recalculée en saisie directe : début + durée x fréquence
  const datDebut = Form.useWatch('DATDEBUT', form);
  const duree = Form.useWatch('DUREE', form);
  const freqProv = Form.useWatch('FREQPROV', form);
  useEffect(() => {
    if (sbmit !== 'add' || !datDebut || !duree || !freqProv) return;
    const fin = new Date(datDebut);
    fin.setDate(fin.getDate() + Number(duree) * joursFreq(freqProv));
    form.setFieldValue('DATFIN', fin.toISOString().substring(0, 10));
  }, [datDebut, duree, freqProv]);

  const toOptions = (list: ICompte[]): DataSelect[] => list.map(c => ({ value: c.NUMCPT, label: `${c.NUMCPT}--${c.NOMCPT}` }))
  // Spec : compte dépôt = tous comptes du client ; compte BDC et compte
  // intérêts filtrés par le plan comptable autorisé du type de BDC.
  // Comptes dépôt / intérêts : filtrés sur la liste des plans autorisés
  // (PARAMBDC_PLANS) — tous les comptes si la liste n'est pas configurée.
  // Compte BDC : toujours filtré par PLANCPTBDC du type.
  const cptPlans = compte.filter(c => plansAutorises.length === 0 || plansAutorises.includes(c.IDPLAN ?? ''))
  const cptOptions = toOptions(cptPlans)
  const cptBdcOptions = toOptions(compte.filter(c => !selectedType?.PLANCPTBDC || c.IDPLAN === selectedType.PLANCPTBDC))
  const cptIntOptions = toOptions(cptPlans)

  // Nombre de jours par fréquence
  const joursFreq = (freq: string) => {
    switch ((freq || "").toUpperCase()) {
      case "A": return 360;
      case "S": return 180;
      case "T": return 90;
      case "M": return 30;
      case "B": return 15;
      case "F": return 60;
      case "H": return 7;
      case "J": return 1;
      default: return 30;
    }
  }

  // Calcule l'échéancier à partir des valeurs du formulaire ou d'une ligne
  function calculer(source?: any) {
    const get = (f: string) => source ? source[f] : form.getFieldValue(f);

    const freq = (get("FREQPROV") || "").toUpperCase();
    const montant = Number(get("MONTANT")) || 0;
    const tauxInteret = Number(get("TXINT")) || 0;
    const tauxTaxe = Number(get("TXTAXE")) || 0;
    const duree = Number(get("DUREE")) || 0;
    const datDebut = get("DATDEBUT") || Perss.datjour;

    if (!freq || !montant || !tauxInteret || !duree) {
      SweetAlert.fire({
        icon: "warning",
        title: "Champs manquants",
        text: "Veuillez renseigner la fréquence, le montant, le taux et la durée.",
        target: document.body,
        backdrop: true,
      });
      return;
    }

    const jours = joursFreq(freq);

    let montIntBrut = (montant * tauxInteret * duree * jours) / 36000;
    let montProvFreq = Math.round(montIntBrut / duree);
    let montInt = montProvFreq * duree;
    let montTaxeBrut = (montInt * tauxTaxe) / 100;
    let montTaxeFreq = Math.round(montTaxeBrut / duree);
    let montTaxe = montTaxeFreq * duree;
    let montNet = montInt - montTaxe;

    let dateEcheance = new Date(datDebut);
    const newEcheancier = [];
    for (let i = 1; i <= duree; i++) {
      const prochaine = new Date(dateEcheance);
      prochaine.setDate(prochaine.getDate() + jours);
      newEcheancier.push({
        key: i,
        periode: i,
        dateImput: dateEcheance.toLocaleDateString(),
        interet: montProvFreq,
        taxe: montTaxeFreq,
        netAPayer: montProvFreq - montTaxeFreq,
      });
      dateEcheance = prochaine;
    }
    setEcheancier(newEcheancier);

    form.setFieldsValue({
      MTNINT: montInt || 0,
      MTNTAXE: montTaxe || 0,
      MTNTAXEFREQ: montTaxeFreq || 0,
      MTNNETAPAYER: montNet || 0,
      MTNPROVFREQ: montProvFreq || 0,
      TotInt: formatNumber(montInt),
      TotTaxe: formatNumber(montTaxe),
      TotNet: formatNumber(montNet),
    });
    fetchFrais(get("TYPBDC"), montant);
    setImpActive(false)
  }

  // Exports de l'échéancier
  const handleExport = (format: any) => {
    const title = "Exportation tableau imputaion interets " + (selected?.NUMBDC || form.getFieldValue("NUMBDC") || "");
    switch (format) {
      case 'excel':
        GlobalExportToExcel(echeancier, title, "TableauInteretsBDC")
        break;
      case 'pdf':
        GlobalExportToPDF(echeancier, title, "TableauInteretsBDC")
        break;
      case 'word':
        GlobalExportToWord(echeancier, title, "TableauInteretsBDC")
        break;
      case 'csv':
        GlobalExportToCSV(echeancier, title, "TableauInteretsBDC")
        break;
      case 'json':
        GlobalExportToJSON(echeancier, title, "TableauInteretsBDC")
        break;
      default:
        break;
    }
  };

  // Remplit le formulaire depuis un bon existant (consultation)
  const fillFromBdc = (row: IBonDeCaisse) => {
    const cleanRow: any = {
      ...row,
      DATDEBUT: fmtDate(row.DATDEBUT),
      DATFIN: fmtDate(row.DATFIN),
      lib_NUMCLI: row.NOMCLI,
      lib_TYPBDC: paramBDC.find(t => t.CODBDC === row.TYPBDC)?.LIBELL || '',
      TotInt: formatNumber(Number(row.MTNINT)),
      TotTaxe: formatNumber(Number(row.MTNTAXE)),
      TotNet: formatNumber(Number(row.MTNNETAPAYER)),
    }
    setSelected(row);
    setSelectedType(paramBDC.find(t => t.CODBDC === row.TYPBDC) || null)
    form.setFieldsValue(cleanRow);
    getComptes(row.NUMCLI)
    fetchPlansAutorises(row.TYPBDC)
    calculer({ ...row })
    fetchFrais(row.TYPBDC, row.MONTANT)
  }

  // Bouton "Load" à côté du champ N° BDC / Code Simul (même flux que le
  // champ Code Simulation de /prets/demande) : charge la simulation ou le
  // bon correspondant au code sélectionné et remplit le formulaire.
  const load = async () => {
    const code = form.getFieldValue("CODEBDC") || form.getFieldValue("NUMBDC")
    if (!code) {
      SweetAlert.fire({ title: "Sélectionnez ou saisissez d'abord un numéro de bon de caisse ou une simulation.", icon: "warning" })
      return
    }
    setLoadingSimul(true)
    const simuls = await getSimulations()
    const simul = simuls.find(s => s.CODSIMUL === code);
    if (simul) {
      setLoadingSimul(false)
      form.setFieldValue("CODSIMUL", simul.CODSIMUL)
      form.setFieldValue("NUMBDC", "")
      fillFromSimul(simul)
      cunsultButton()
      return
    }
    const list = await fetchData()
    setLoadingSimul(false)
    const row = list.find(c => c.NUMBDC === code)
    if (!row) {
      SweetAlert.fire({ title: "Bon de caisse introuvable", text: "Aucune simulation ni bon de caisse pour : " + code, icon: "warning" })
      return
    }
    form.setFieldValue("NUMBDC", row.NUMBDC)
    fillFromBdc(row)
    cunsultButton()
  }

  // Consulter : même chargement que le bouton "Load" du champ
  const consult = async () => {
    await load()
  }

  useEffect(() => {
    getSimulations()
    fetchDataTypeBDC()
    form.setFieldsValue({ DATDEBUT: Perss.datjour, DATFIN: Perss.datjour })
  }, []);

  // ------------------------------------------------------------------
  // Soumission
  // ------------------------------------------------------------------
  const onFinish = async () => {
    // (true) : inclut les valeurs du store sans Form.Item (MTNINT, MTNTAXE, ...)
    const values = form.getFieldsValue(true);
    const valuesBdc: IBonDeCaisse = { ...values }
    valuesBdc.NOMCLI = values.lib_NUMCLI || values.NOMCLI
    valuesBdc.CODAGE = Perss.codage
    valuesBdc.UTISAI = Perss.coduti
    valuesBdc.DATCRE = new Date()
    valuesBdc.DATVAL = values.DATVAL || new Date(Perss.datjour)
    valuesBdc.HEUREVAL = values.HEUREVAL || new Date().toISOString()
    valuesBdc.CDETAT = values.CDETAT || "01"
    valuesBdc.CODOPE = values.CODOPE || "990"
    valuesBdc.PENALITE = 0;
    valuesBdc.INTACQUIS = 0;
    valuesBdc.INTDUS = 0;
    // Les champs calculés sont stockés via setFieldsValue sans Form.Item
    // monté — getFieldsValue peut ne pas les retourner : on les coercis ici.
    valuesBdc.IDBDC = Number(valuesBdc.IDBDC) || 0;
    valuesBdc.DUREE = Number(valuesBdc.DUREE) || 0;
    valuesBdc.MONTANT = Number(valuesBdc.MONTANT) || 0;
    valuesBdc.TXINT = Number(valuesBdc.TXINT) || 0;
    valuesBdc.TXTAXE = Number(valuesBdc.TXTAXE) || 0;
    valuesBdc.MTNINT = Number(valuesBdc.MTNINT) || 0;
    valuesBdc.MTNTAXE = Number(valuesBdc.MTNTAXE) || 0;
    valuesBdc.MTNPROVFREQ = Number(valuesBdc.MTNPROVFREQ) || 0;
    valuesBdc.MTNTAXEFREQ = Number(valuesBdc.MTNTAXEFREQ) || 0;
    valuesBdc.MTNNETAPAYER = Number(valuesBdc.MTNNETAPAYER) || 0;
    Object.keys(valuesBdc).forEach(k => { if (k.startsWith('lib_') || k.startsWith('Tot')) delete (valuesBdc as any)[k] })

    if (!valuesBdc.NUMCLI || !valuesBdc.TYPBDC) {
      SweetAlert.fire({
        icon: "warning",
        title: "Champs manquants",
        text: "Veuillez sélectionner le client et le type de bon de caisse.",
        target: document.body,
        backdrop: true,
      });
      return;
    }

    if (!valuesBdc.MONTANT || !valuesBdc.FREQPROV || !valuesBdc.DUREE || !valuesBdc.DATDEBUT || !valuesBdc.DATFIN) {
      SweetAlert.fire({
        icon: "warning",
        title: "Champs manquants",
        text: "Veuillez renseigner le montant, la fréquence, la durée et les dates.",
        target: document.body,
        backdrop: true,
      });
      return;
    }

    // Si l'échéancier n'a pas été calculé avant l'enregistrement, on le
    // calcule ici pour remplir MTNINT/MTNTAXE/MTNPROVFREQ/MTNNETAPAYER.
    if (!valuesBdc.MTNPROVFREQ) {
      calculer();
      const recalc = form.getFieldsValue(true);
      valuesBdc.MTNINT = Number(recalc.MTNINT) || 0;
      valuesBdc.MTNTAXE = Number(recalc.MTNTAXE) || 0;
      valuesBdc.MTNPROVFREQ = Number(recalc.MTNPROVFREQ) || 0;
      valuesBdc.MTNTAXEFREQ = Number(recalc.MTNTAXEFREQ) || 0;
      valuesBdc.MTNNETAPAYER = Number(recalc.MTNNETAPAYER) || 0;
    }

    // AUTOCPTBDC='O' : le compte BDC est généré côté backend (ou réutilisé
    // si renouvellement via REFBDC) — pas de sélection requise.
    const cptBdcManquant = selectedType?.AUTOCPTBDC === 'O' ? false : !valuesBdc.CPTBDC;
    if (!valuesBdc.CPTDEPOT || cptBdcManquant || !valuesBdc.CPTINTCLI) {
      SweetAlert.fire({
        icon: "warning",
        title: "Comptes manquants",
        text: "Veuillez sélectionner le compte de dépôt, le compte bon de caisse et le compte intérêts.",
        target: document.body,
        backdrop: true,
      });
      return;
    }

    if (sbmit == "add") {
      const result = await SweetAlert.fire({
        title: "Création bon de caisse",
        text: valuesBdc.CODSIMUL
          ? `Voulez-vous créer le bon de caisse à partir de la simulation : ${valuesBdc.CODSIMUL} ?`
          : `Voulez-vous créer le bon de caisse pour le client ${valuesBdc.NUMCLI} (montant : ${formatNumber(Number(valuesBdc.MONTANT))}) ?`,
        showCancelButton: true,
        confirmButtonText: "Oui",
        cancelButtonText: "Non",
        icon: "question",
        confirmButtonColor: "#116599",
        cancelButtonColor: "darkred",
        target: document.body,
      });

      if (result.isConfirmed) {
        setLoading(true);
        let data = Crypter.encrypt(JSON.stringify(valuesBdc));
        let config = {
          method: 'post',
          maxBodyLength: Infinity,
          url: Data.ApiUrl.CreateBonDeCaisse,
          headers: {
            'CypherBonDeCaisse': data,
            'Authorization': authHeader(),
            'Content-Type': 'application/json'
          }
        };
        axios.request(config)
          .then((response) => {
            setLoading(false);
            if (response.data) {
              const res = JSON.parse(Crypter.decrypt(response.data));
              let code = res.Result
              if (res.StatutCode == 200) {
                if (code == 'Existe') {
                  SweetAlert.fire({ title: res.Message, text: "Ce code existe déja svp choisissez un autre", icon: "warning" })
                } else {
                  if (code) form.setFieldValue("NUMBDC", code)
                  fetchData();
                  SweetAlert.fire({ title: res.Message, text: "Code : " + code, icon: "success" })
                  setSbmit("")
                  initialiazeButton()
                  cunsultButton()
                }
              }
              else {
                SweetAlert.fire({ title: "Warning", text: res.Message, icon: "warning" })
              }
            }
          })
          .catch((error) => {
            setLoading(false);
            console.log(error)
            SweetAlert.fire({ title: "Error Front", text: error, icon: "error" })
          });
      }
    }

    if (sbmit == "update") {
      if (selected && selected.CDETAT != '01') {
        SweetAlert.fire({
          icon: "warning",
          title: "Modification bon de caisse",
          text: "Impossible de mofifier ce bon de caisse: il a déjà fait l'objet d'une validation",
          target: document.body,
          backdrop: true,
        });
        return;
      }

      const result = await SweetAlert.fire({
        title: "Modification du bon de caisse",
        text: `Voulez-vous modifier le bon de caisse : ${valuesBdc.NUMBDC} ?`,
        showCancelButton: true,
        confirmButtonText: "Oui",
        cancelButtonText: "Non",
        icon: "question",
        confirmButtonColor: "#116599",
        cancelButtonColor: "darkred",
        target: document.body,
      });

      if (result.isConfirmed) {
        setLoading(true);
        let data = Crypter.encrypt(JSON.stringify(valuesBdc));
        let config = {
          method: 'put',
          maxBodyLength: Infinity,
          url: Data.ApiUrl.UpdateBonDeCaisse,
          headers: {
            'CypherBonDeCaisse': data,
            'Authorization': authHeader(),
            'Content-Type': 'application/json'
          },
          data: data
        };

        axios.request(config)
          .then((response) => {
            setLoading(false);
            if (response.data) {
              const res = JSON.parse(Crypter.decrypt(response.data));
              if (res.StatutCode == 200) {
                let code = res.Result
                if (code == 'INCONNU') {
                  SweetAlert.fire({ title: res.Message, text: "Ce code n'existe pas", icon: "warning" })
                } else {
                  fetchData();
                  SweetAlert.fire({ title: res.Message, text: "Code : " + code, icon: "success" })
                  setSbmit("")
                  initialiazeButton()
                  cunsultButton()
                }
              }
              else {
                SweetAlert.fire({ title: "Warning", text: res.Message, icon: "warning" })
              }
            }
          })
          .catch((error) => {
            setLoading(false);
            console.log(error)
            SweetAlert.fire({ title: "Error Front", text: error, icon: "error" })
          });
      }
    }
  };

  const onFinishFailed = async (errorInfo: any) => {
    var errors: any = [];
    errorInfo.errorFields.map((error: any) => {
      errors.push(error.name + "");
    })
    SweetAlert.fire({ title: "Veuillez remplir tous les champs obligatoires", text: errors.toString().replaceAll(',', " - "), icon: 'warning', confirmButtonText: 'OK' })
  };

  // ------------------------------------------------------------------
  // Onglet mise en place (même layout que /prets/simulation)
  // ------------------------------------------------------------------
  const items: TabsProps['items'] = [
    {
      key: '1',
      label: (
        <span id='Tab'>
          <UserOutlined />
          Mise en place bon de caisse
        </span>
      ),
      children: (
        <div className='tabItem'>
          <div >
            <Box fontStyle="inherit" display={"flex"}>
              <SearchInput notRequired notremove enable width={7} form={form} Titre='Bon de caisse / Simulation' label='N° BDC / Code Simul' name='CODEBDC' Parametre={searchCodeBdc} />
              <Button onClick={load} style={{ top: '10px' }} className='bt_add' type="ghost">{loadingSimul ? <CircularProgress size={"20px"} /> : "Load"}</Button>
            </Box>
            <Box fontStyle="inherit" className='border' width={"85%"} position={"absolute"}>
              <Box display={"flex"} fontStyle="inherit" className='border-left'>

                <Box fontStyle="inherit" className='border-left'>
                  <InputForm width={11} label='N° BDC' name='NUMBDC' type='label' notRequired />
                  <InputForm width={11} label='Code Simul' name='CODSIMUL' type='label' notRequired />
                  <SearchInput dissabledCode={!ajoutActve} notremove width={7} form={form} Titre='Recherche client' label='Indice client' name='NUMCLI' Parametre={searchClient} />
                  <InputForm width={11} label='Nom client' name='lib_NUMCLI' type='label' notRequired />
                  <SearchInput dissabledCode={!ajoutActve} dissabledLib={true} notremove notRequired width={7} form={form} Titre='Ancien bon de caisse (renouvellement)' label='Réf. BDC (renouvellement)' name='REFBDC' Parametre={searchBdc} />
                </Box>

                <Box fontStyle="inherit" className='border-left'>
                  <SearchInput dissabledCode={!ajoutActve} notremove width={7} form={form} Titre='Type de bon de caisse' label='Type BDC' name='TYPBDC' Parametre={searchTypeBdc} />
                  <InputForm width={11} label='Type imputation' name='TYPIMPUTINT' type='label' notRequired />
                  <SearchModal notremove disabled={!ajoutActve} width={11} Taille1="35%" Taille2="65%" form={form} Titre='Recherche périodicité' label='Fréquence' name='FREQPROV' Parametre={searchFreq} />
                  <InputForm width={11} label='Durée' name='DUREE' type='number' notRequired />
                </Box>

                <Box fontStyle="inherit" className='border-left'>
                  <InputForm width={11} label='Montant' name='MONTANT' type='number' notRequired />
                  <InputForm width={11} label='Date début' name='DATDEBUT' type='date' notRequired />
                  <InputForm width={11} label='Date fin' name='DATFIN' type='date' notRequired />
                  <InputForm width={11} label='Taux intérêt' name='TXINT' type='number' notRequired />
                  <InputForm width={11} label='Taux taxe' name='TXTAXE' type='number' notRequired />
                </Box>

                <Box fontStyle="inherit" className='border-left'>
                  <SelectOption width={11} notRequired name="CPTDEPOT" Option={cptOptions} label="Compte dépôt" />
                  <InputForm width={11} label='Disponible' name='DISPONCPTDEPOT' type='label' notRequired />
                  {selectedType?.AUTOCPTBDC === 'O'
                    ? <InputForm width={11} label='Compte BDC (généré à la mise en place)' name='CPTBDC' type='label' notRequired />
                    : <SelectOption width={11} notRequired name="CPTBDC" Option={cptBdcOptions} label="Compte BDC" />}
                  <InputForm width={11} label='Disponible' name='DISPONCPTBDC' type='label' notRequired />
                  <SelectOption width={11} notRequired name="CPTINTCLI" Option={cptIntOptions} label="Compte intérêts" />
                  <InputForm width={11} label='Disponible' name='DISPONCPTINT' type='label' notRequired />
                </Box>

              </Box>
            </Box>
          </div>
        </div>
      ),
    }
  ];

  return (
    <div className='tab'>
      <Typography align='center' className='formTitle'><b>MISE EN PLACE BON DE CAISSE</b></Typography>
      <Spin spinning={loading} size='large' style={{ color: '#116599' }} >
        <Form disabled={!ajoutActve} autoComplete="off" labelAlign='left' form={form} name="dynamic_form_complex" onFinish={onFinish} onFinishFailed={onFinishFailed} style={{ minWidth: 'auto', maxWidth: 'auto' }}>

          <div className='bG'>
            <legend><Typography color={"#116599"}>Actions</Typography></legend>
            <Form.Item shouldUpdate className='bouttong'>
              <Space direction="vertical" style={{ width: '100%' }}>
                <Button size='large' className='bt_add' disabled={ajoutActve} onClick={SetStart} type="ghost" htmlType="button" block  >
                  Ajouter
                </Button>
                <Button size='large' className='bt_add' disabled={consulActive} onClick={consult} type="ghost" htmlType="button" block  >
                  Consulter
                </Button>
                <Button size='large' className='bt_add' disabled={enregActve} type="ghost" onClick={() => calculer()} block>
                  Échéancier
                </Button>
                <ExportMenu disabled={impActive} onExport={handleExport} />
                <Button size='large' className='bt_add' disabled={modifActve} onClick={modifButton} type="ghost" htmlType="button" block  >
                  Modifier
                </Button>
                <Button size='large' className='bt_add' disabled={enregActve} type="ghost" htmlType="submit" block>
                  Enregistrer
                </Button>
                <Button size='large' disabled={annulActive} onClick={handleAnulle} className='bt_add' type="ghost" htmlType="reset" block>
                  Annuler
                </Button>
              </Space>
            </Form.Item>
          </div>
          <div className='clientP'>
            <div >
              <Tabs size='small' type="card" defaultActiveKey="1" items={items} />
              <Box fontStyle="inherit" className='tabElement' style={{ top: '63%', height: 'auto' }}>
                <Spin spinning={loading}>
                  <Table size='small' style={{ width: 'auto' }} columns={columns} dataSource={echeancier} scroll={{ y: 200, x: 10 }} pagination={false} />
                </Spin>
                <Box display={"flex"} fontStyle="inherit" className='border-left bordure1' style={{ marginTop: 8 }}>
                  <InputForm notRequired disabled width={8} label='Intérêts bruts' name='TotInt' type='text' />
                  <InputForm notRequired disabled width={8} label='Taxe retenue' name='TotTaxe' type='text' />
                  <InputForm notRequired disabled width={8} label='Net versé au client' name='TotNet' type='text' />
                  <InputForm notRequired disabled width={8} label='Frais souscription' name='FRAISSOUSCR' type='text' />
                </Box>
              </Box>
            </div>
            <div>
            </div>
          </div>
        </Form>
      </Spin>

    </div>
  );
}
